import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Route,
  Ship,
  Train,
  Truck,
  Plane,
  BadgeCheck,
  Clock,
  FileText,
  AlertTriangle,
  Layers,
  MapPinned,
  Search,
} from 'lucide-react';
import TopStatsNav from '../components/TopStatsNav';
import MapIrMap from '../components/MapIrMap';
import { ROUTES } from '../lib/routes';

const nfFa = new Intl.NumberFormat('fa-IR');

const MODE_META = {
  maritime: { title: 'دریایی', icon: Ship, pill: 'bg-blue-50 text-blue-700 border-blue-100' },
  rail: { title: 'ریلی', icon: Train, pill: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  road: { title: 'جاده‌ای', icon: Truck, pill: 'bg-amber-50 text-amber-700 border-amber-100' },
  air: { title: 'هوایی', icon: Plane, pill: 'bg-purple-50 text-purple-700 border-purple-100' },
};

function faDigits(str) {
  return String(str || '').replace(/\d/g, (d) => nfFa.format(Number(d)));
}

function statusBadge(status) {
  const map = {
    'ثبت شده': { cls: 'bg-gray-50 text-gray-700 border-gray-100', icon: FileText },
    'در حال حمل': { cls: 'bg-blue-50 text-blue-700 border-blue-100', icon: Route },
    'در مرز/بندر': { cls: 'bg-amber-50 text-amber-700 border-amber-100', icon: MapPinned },
    'تحویل شد': { cls: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: BadgeCheck },
    'دارای هشدار': { cls: 'bg-red-50 text-red-700 border-red-100', icon: AlertTriangle },
  };
  return map[status] || map['در حال حمل'];
}

const SAMPLE_SHIPMENTS = [
  {
    id: 'CNS-TRK-2401-0092',
    title: 'ترانزیت کانتینری — بندرعباس → تهران',
    status: 'در حال حمل',
    modes: ['maritime', 'rail', 'road'],
    incoterm: 'CPT',
    cargo: 'قطعات صنعتی (پالت)',
    weightKg: 18400,
    volumeM3: 38,
    updatedAt: '۱۴۰۴/۱۱/۰۹ - ۱۳:۲۰',
    eta: '۱۴۰۴/۱۱/۱۱ - ۱۷:۰۰',
    route: [
      { name: 'بندر شهید رجایی (بندرعباس)', lat: 27.11, lng: 56.06, kind: 'origin' },
      { name: 'ایستگاه ریلی بندرعباس', lat: 27.2, lng: 56.1, kind: 'handoff' },
      { name: 'ایستگاه ریلی یزد', lat: 31.9, lng: 54.37, kind: 'checkpoint' },
      { name: 'ایستگاه ریلی تهران', lat: 35.69, lng: 51.4, kind: 'handoff' },
      { name: 'انبار مقصد (تهران)', lat: 35.74, lng: 51.5, kind: 'destination' },
    ],
    milestones: [
      { t: 'ثبت سفارش', s: 'انجام شد', at: '۱۴۰۴/۱۱/۰۸ - ۱۰:۱۲' },
      { t: 'تخصیص ناوگان', s: 'انجام شد', at: '۱۴۰۴/۱۱/۰۸ - ۱۲:۴۵' },
      { t: 'تحویل به اپراتور ریلی', s: 'در حال انجام', at: '۱۴۰۴/۱۱/۰۹ - ۰۸:۳۰' },
      { t: 'ورود به تهران', s: 'زمان‌بندی شده', at: '۱۴۰۴/۱۱/۱۱ - ۱۲:۰۰' },
      { t: 'تحویل نهایی', s: 'زمان‌بندی شده', at: '۱۴۰۴/۱۱/۱۱ - ۱۷:۰۰' },
    ],
    docs: [
      { name: 'بارنامه ترکیبی (FIATA)', state: 'موجود' },
      { name: 'بیمه‌نامه', state: 'موجود' },
      { name: 'گواهی مبدا', state: 'در صورت نیاز' },
    ],
    alerts: [],
  },
  {
    id: 'CNS-TRK-2401-0137',
    title: 'ترانزیت چندوجهی — مسکو → بندرعباس (از طریق دریای کاسپین)',
    status: 'در مرز/بندر',
    modes: ['rail', 'maritime', 'road'],
    incoterm: 'CPT',
    cargo: 'قطعات صنعتی (پالت) — ترانزیت کاسپین',
    weightKg: 12400,
    volumeM3: 28,
    updatedAt: '۱۴۰۴/۱۱/۰۹ - ۱۵:۰۵',
    eta: '۱۴۰۴/۱۱/۱۳ - ۱۶:۳۰',
    route: [
      { name: 'مسکو (روسیه)', lat: 55.7558, lng: 37.6173, kind: 'origin' },
      { name: 'بندر آستراخان (کاسپین)', lat: 46.3479, lng: 48.0336, kind: 'handoff' },
      { name: 'بندر انزلی (کاسپین)', lat: 37.4723, lng: 49.4622, kind: 'handoff' },
      { name: 'بندر امیرآباد (کاسپین)', lat: 36.8000, lng: 52.3500, kind: 'checkpoint' },
      { name: 'بندرعباس (مقصد)', lat: 27.1832, lng: 56.2666, kind: 'destination' },
    ],
    milestones: [
      { t: 'ثبت سفارش', s: 'انجام شد', at: '۱۴۰۴/۱۱/۰۹ - ۰۹:۱۰' },
      { t: 'تحویل به ریلی (مبدا)', s: 'انجام شد', at: '۱۴۰۴/۱۱/۰۹ - ۱۲:۰۰' },
      { t: 'ورود به بندر آستراخان', s: 'در حال انجام', at: '۱۴۰۴/۱۱/۱۰ - ۰۸:۳۰' },
      { t: 'عبور دریای کاسپین', s: 'زمان‌بندی شده', at: '۱۴۰۴/۱۱/۱۱ - ۰۶:۰۰' },
      { t: 'ورود به بنادر کاسپین ایران', s: 'زمان‌بندی شده', at: '۱۴۰۴/۱۱/۱۱ - ۱۸:۰۰' },
      { t: 'حمل زمینی تا بندرعباس', s: 'زمان‌بندی شده', at: '۱۴۰۴/۱۱/۱۲ - ۰۹:۰۰' },
      { t: 'تحویل نهایی', s: 'زمان‌بندی شده', at: '۱۴۰۴/۱۱/۱۳ - ۱۶:۳۰' },
    ],
    docs: [
      { name: 'بارنامه/CMR', state: 'موجود' },
      { name: 'بارنامه دریایی (کاسپین)', state: 'در حال بررسی' },
      { name: 'بیمه‌نامه ترانزیت', state: 'موجود' },
    ],
    alerts: [{ title: 'بنادر کاسپین', body: 'مسیر نمونه از آستراخان → انزلی → امیرآباد عبور می‌کند (قابل تغییر با ظرفیت/آبخور).' }],
  },
  {
    id: 'CNS-TRK-2401-0204',
    title: 'حمل ترکیبی — آستارا → قزوین',
    status: 'دارای هشدار',
    modes: ['road', 'rail'],
    incoterm: 'FCA',
    cargo: 'مواد اولیه (کیسه)',
    weightKg: 26500,
    volumeM3: 42,
    updatedAt: '۱۴۰۴/۱۱/۰۹ - ۱۱:۴۰',
    eta: '۱۴۰۴/۱۱/۱۰ - ۲۰:۰۰',
    route: [
      { name: 'مرز آستارا', lat: 38.43, lng: 48.87, kind: 'origin' },
      { name: 'ایستگاه ریلی رشت', lat: 37.29, lng: 49.59, kind: 'handoff' },
      { name: 'ایستگاه ریلی قزوین', lat: 36.27, lng: 50.0, kind: 'destination' },
    ],
    milestones: [
      { t: 'ثبت سفارش', s: 'انجام شد', at: '۱۴۰۴/۱۱/۰۸ - ۱۸:۰۲' },
      { t: 'ورود به مرز', s: 'انجام شد', at: '۱۴۰۴/۱۱/۰۹ - ۰۸:۵۰' },
      { t: 'کنترل اسناد', s: 'نیاز به اقدام', at: '۱۴۰۴/۱۱/۰۹ - ۱۱:۱۵' },
      { t: 'تحویل به ریلی', s: 'زمان‌بندی شده', at: '۱۴۰۴/۱۱/۱۰ - ۰۹:۰۰' },
      { t: 'تحویل نهایی', s: 'زمان‌بندی شده', at: '۱۴۰۴/۱۱/۱۰ - ۲۰:۰۰' },
    ],
    docs: [
      { name: 'پکینگ لیست', state: 'موجود' },
      { name: 'بارنامه جاده‌ای', state: 'موجود' },
      { name: 'مجوز استاندارد', state: 'ناقص' },
    ],
    alerts: [{ title: 'نقص سند', body: 'مجوز استاندارد بارگذاری نشده؛ برای جلوگیری از توقف، تکمیل کنید.' }],
  },
];

export default function MultimodalTracking() {
  const navigate = useNavigate();
  const [modeFilter, setModeFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(SAMPLE_SHIPMENTS[0].id);

  useEffect(() => {
    // Leaflet icons not needed; kept as a placeholder for potential future Leaflet usage.
  }, []);

  const shipments = useMemo(() => {
    const q = String(query || '').trim();
    return SAMPLE_SHIPMENTS.filter((s) => {
      if (modeFilter !== 'all' && !s.modes.includes(modeFilter)) return false;
      if (!q) return true;
      return (
        s.id.includes(q) ||
        s.title.includes(q) ||
        s.cargo.includes(q)
      );
    });
  }, [modeFilter, query]);

  const selected = useMemo(
    () => shipments.find((s) => s.id === selectedId) || shipments[0] || SAMPLE_SHIPMENTS[0],
    [shipments, selectedId]
  );

  const path = selected.route.map((p) => [p.lat, p.lng]);
  const center = path[Math.floor(path.length / 2)] || [32.0, 53.7];

  return (
    <div className="min-h-screen bg-[#f8fafc] font-[IRANSansWeb,Tahoma] p-6 md:p-10" dir="rtl">
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 md:p-10 overflow-hidden relative">
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.10),transparent_70%)]" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="text-right">
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-2xl text-[11px] font-extrabold border border-emerald-100">
                <Layers size={16} />
                <span>رهگیری چندوجهی (دریایی/ریلی/جاده‌ای/هوایی)</span>
              </div>

              <h1 className="mt-6 text-3xl md:text-4xl font-black text-gray-900 leading-tight">
                رهگیری و کنترل عملیات حمل
              </h1>

              <p className="mt-4 text-sm text-gray-500 leading-8">
                با انتخاب محموله، مسیر روی نقشه نمایش داده می‌شود و وضعیت مرحله‌ای (تحویل‌ها، تغییر مد حمل، کنترل اسناد و ETA) را
                به‌صورت یکپارچه مشاهده می‌کنید.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="h-12 px-5 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold text-sm transition inline-flex items-center justify-center gap-2"
                >
                  <ArrowRight size={18} />
                  بازگشت به داشبورد
                </button>

                <button
                  type="button"
                  onClick={() => navigate(ROUTES.services.road)}
                  className="h-12 px-5 rounded-2xl bg-[#0f766e] hover:bg-[#115e59] text-white font-bold text-sm transition inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/15"
                >
                  مشاهده خدمات مستقیم
                  <ArrowLeft size={18} />
                </button>
              </div>
            </div>

            <div className="bg-[#fbfdff] border border-gray-100 rounded-[2.5rem] p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="text-right">
                  <div className="text-[11px] text-gray-400 font-bold">کد رهگیری منتخب</div>
                  <div className="mt-2 text-lg font-black text-gray-800">{selected.id}</div>
                  <div className="mt-1 text-[11px] text-gray-500 leading-6">{selected.title}</div>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700">
                  <Route size={22} />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                <Kpi label="آخرین به‌روزرسانی" value={selected.updatedAt} icon={<Clock size={16} />} />
                <Kpi label="ETA" value={selected.eta} icon={<BadgeCheck size={16} />} />
                <Kpi label="وزن" value={`${nfFa.format(selected.weightKg)} کیلوگرم`} icon={<Truck size={16} />} />
                <Kpi label="حجم" value={`${nfFa.format(selected.volumeM3)} مترمکعب`} icon={<Ship size={16} />} />
              </div>
            </div>
          </div>
        </header>

        <TopStatsNav
          items={[
            {
              label: 'بازارچه خدمات مستقیم',
              value: 4,
              unit: 'مد فعال',
              route: ROUTES.services.maritime,
              accent: 'emerald',
              sparkValues: [3, 4, 6, 5, 7, 9],
            },
            {
              label: 'مشوق‌ها و مدل‌های مشارکت',
              value: 3,
              unit: 'مدل پیشنهادی',
              route: ROUTES.systems.permitsPartnership,
              accent: 'amber',
              sparkValues: [2, 3, 3, 4, 5, 6],
            },
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 md:p-8 border-b border-gray-100">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="text-right">
                  <h2 className="text-lg font-black text-gray-800">نقشه مسیر و نقاط کلیدی</h2>
                  <p className="text-[11px] text-gray-400 mt-2 leading-6">
                    نمایش مسیر تقریبی (نمونه) و نقاط تعویض مد/ایستگاه‌ها/مقصد. برای محیط واقعی، داده‌ها از سرویس رهگیری دریافت می‌شود.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <ModePill active={modeFilter === 'all'} onClick={() => setModeFilter('all')} label="همه" />
                  {Object.entries(MODE_META).map(([key, meta]) => (
                    <ModePill
                      key={key}
                      active={modeFilter === key}
                      onClick={() => setModeFilter(key)}
                      label={meta.title}
                      Icon={meta.icon}
                      className={meta.pill}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-5 relative">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="جستجو: کد رهگیری، عنوان، نوع کالا..."
                  className="w-full h-12 pr-12 pl-4 rounded-2xl border border-gray-200 bg-white focus:outline-none focus:border-emerald-400 text-sm"
                />
                <Search className="absolute right-4 top-3 text-gray-400" size={20} />
              </div>
            </div>

            <div className="h-[420px]">
              <MapIrMap
                className="h-full w-full"
                centerLatLng={center}
                zoom={5}
                lineLatLngs={path}
                lineColor="#0f766e"
                lineWidth={4}
                markers={selected.route.map((p) => ({
                  id: `${selected.id}-${p.name}`,
                  lat: p.lat,
                  lng: p.lng,
                  title: p.name,
                  subtitle: faDigits(`${p.lat.toFixed(3)}, ${p.lng.toFixed(3)}`),
                }))}
              />
            </div>

            <div className="p-6 md:p-8 border-t border-gray-100 bg-[#fbfdff]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCard
                  title="مدهای حمل در این پرونده"
                  icon={<Layers size={18} className="text-emerald-700" />}
                  body={
                    <div className="flex flex-wrap gap-2">
                      {selected.modes.map((m) => {
                        const meta = MODE_META[m];
                        const Icon = meta.icon;
                        return (
                          <span
                            key={m}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl border text-[11px] font-extrabold ${meta.pill}`}
                          >
                            <Icon size={14} />
                            {meta.title}
                          </span>
                        );
                      })}
                    </div>
                  }
                />
                <InfoCard
                  title="اسناد کلیدی"
                  icon={<FileText size={18} className="text-emerald-700" />}
                  body={
                    <div className="space-y-2">
                      {selected.docs.map((d) => (
                        <div key={d.name} className="flex items-center justify-between gap-3">
                          <div className="text-[12px] font-bold text-gray-800">{d.name}</div>
                          <span className="text-[10px] font-extrabold bg-white border border-gray-100 text-gray-600 px-3 py-1.5 rounded-full">
                            {d.state}
                          </span>
                        </div>
                      ))}
                    </div>
                  }
                />
              </div>
            </div>
          </section>

          <aside className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h3 className="text-lg font-black text-gray-800">لیست محموله‌ها</h3>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">نمونه داده برای نمایش UI (قابل اتصال به API).</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700">
                <MapPinned size={22} />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {shipments.map((s) => {
                const b = statusBadge(s.status);
                const Icon = b.icon;
                const active = s.id === selected.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelectedId(s.id)}
                    className={`w-full text-right p-4 rounded-2xl border transition ${
                      active ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-100 bg-[#fbfdff] hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[12px] font-black text-gray-800 truncate">{s.id}</div>
                        <div className="text-[11px] text-gray-500 mt-1 leading-6 line-clamp-2">{s.title}</div>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-[10px] font-extrabold ${b.cls} flex-shrink-0`}>
                        <Icon size={12} />
                        {s.status}
                      </span>
                    </div>
                    <div className="mt-3 text-[10px] text-gray-400 font-bold flex items-center justify-between gap-3">
                      <span>{s.cargo}</span>
                      <span className="whitespace-nowrap">ETA: {s.eta}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 rounded-2xl border border-gray-100 bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="text-right">
                  <div className="text-sm font-black text-gray-800">وضعیت مرحله‌ای</div>
                  <div className="text-[11px] text-gray-400 mt-1 leading-6">برای کاهش توقف‌ها، مراحل و نقاط کنترل را پیگیری کنید.</div>
                </div>
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700">
                  <Route size={18} />
                </div>
              </div>

              <div className="mt-5 space-y-3">
                {selected.milestones.map((m) => (
                  <div key={`${selected.id}-${m.t}`} className="flex items-start gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-[12px] font-extrabold text-gray-800">{m.t}</div>
                        <div className="text-[10px] font-extrabold bg-gray-50 border border-gray-100 text-gray-600 px-3 py-1.5 rounded-full whitespace-nowrap">
                          {m.s}
                        </div>
                      </div>
                      <div className="text-[10px] text-gray-400 mt-1 font-bold">{m.at}</div>
                    </div>
                  </div>
                ))}
              </div>

              {!!selected.alerts.length && (
                <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 p-4">
                  <div className="flex items-start gap-2 text-red-700">
                    <AlertTriangle size={16} className="mt-0.5" />
                    <div className="text-right">
                      <div className="text-[12px] font-black">هشدارها</div>
                      <div className="mt-2 space-y-2">
                        {selected.alerts.map((a) => (
                          <div key={a.title} className="text-[11px] text-red-700/90 leading-6">
                            <span className="font-extrabold">{a.title}:</span> {a.body}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 flex items-start justify-between gap-4">
      <div className="text-right min-w-0">
        <div className="text-[10px] text-gray-400 font-bold">{label}</div>
        <div className="text-[12px] font-extrabold text-gray-800 mt-1 leading-6">{value}</div>
      </div>
      <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700 flex-shrink-0">
        {icon}
      </div>
    </div>
  );
}

function ModePill({ active, onClick, label, Icon, className = 'bg-gray-50 text-gray-700 border-gray-100' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl border text-[11px] font-extrabold transition ${
        active ? 'ring-2 ring-emerald-200' : 'hover:shadow-sm'
      } ${className}`}
    >
      {Icon ? <Icon size={14} /> : null}
      {label}
    </button>
  );
}

function InfoCard({ title, icon, body }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="text-right">
          <div className="text-sm font-black text-gray-800">{title}</div>
        </div>
        <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className="mt-4">{body}</div>
    </div>
  );
}

